import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planOptions = [
  { value: 'Connect One', label: 'Connect One', desc: 'Personal Wellness' },
  { value: 'Connect Duo', label: 'Connect Duo', desc: 'Couples & Partners' },
  { value: 'Connect Family', label: 'Connect Family', desc: 'Family Connection' },
  { value: 'Connect Care', label: 'Connect Care', desc: 'Parents & Seniors' },
  { value: 'Pet Band', label: 'Pet Band', desc: 'Pet Wellness' },
  { value: 'Not sure yet', label: 'Not sure yet', desc: "We'll help you choose" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.08, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const ReservePage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [planInterest, setPlanInterest] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }

    setIsLoading(true);
    try {
      const payload = { name, email };
      if (planInterest) payload.plan_interest = planInterest;

      const response = await axios.post(`${API_URL}/api/waitlist`, payload);
      if (response.data.success) {
        setIsSuccess(true);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFA]" data-testid="reserve-page">
      <Navigation />

      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-xl mx-auto px-6">

          {/* Back link */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-[#7A7A7A] hover:text-[#1A1A1A] transition-colors mb-12"
              data-testid="reserve-back-link"
            >
              <span>&larr;</span>
              <span>Back to Home</span>
            </Link>
          </motion.div>

          {isSuccess ? (
            /* ── Success State ── */
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center py-12 md:py-20"
              data-testid="reserve-success"
            >
              <div className="w-14 h-14 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-7">
                <CheckCircle className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold text-[#1A1A1A] mb-4 leading-tight">
                You're in.
              </h1>
              <p className="text-base text-[#5A5A5A] leading-relaxed mb-2 max-w-sm mx-auto">
                You're on the Connect early access list.
              </p>
              <p className="text-sm text-[#7A7A7A] leading-relaxed mb-10 max-w-sm mx-auto">
                We'll keep you updated with launch news and exclusive early supporter benefits. A confirmation email is on its way.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#1A1A1A] hover:text-[#5A5A5A] transition-colors"
              >
                <span>Explore Connect</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ) : (
            /* ── Form State ── */
            <div>
              {/* Header */}
              <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp} className="text-center mb-10">
                <p className="text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Early Access</p>
                <h1
                  className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A1A1A] mb-4 leading-tight"
                  data-testid="reserve-headline"
                >
                  Reserve your early access
                </h1>
                <p className="text-base text-[#5A5A5A] leading-relaxed max-w-md mx-auto">
                  Join our early access list for launch updates, product previews, and priority reservation opportunities.
                </p>
              </motion.div>

              {/* Form */}
              <motion.form
                custom={1}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                onSubmit={handleSubmit}
                data-testid="reserve-form"
              >
                {/* Name */}
                <div className="mb-0 border-b border-black/10">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-transparent text-[#1A1A1A] placeholder:text-[#9A9A9A] px-0 py-4 text-base
                               outline-none transition-colors focus:placeholder:text-[#5A5A5A]"
                    data-testid="reserve-name-input"
                  />
                </div>

                {/* Email */}
                <div className="mb-8 border-b border-black/10">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className="w-full bg-transparent text-[#1A1A1A] placeholder:text-[#9A9A9A] px-0 py-4 text-base
                               outline-none transition-colors focus:placeholder:text-[#5A5A5A]"
                    data-testid="reserve-email-input"
                  />
                </div>

                {/* Plan Selection — Radio Cards */}
                <div className="mb-8">
                  <p className="text-sm text-[#5A5A5A] mb-4">Which Connect experience interests you most?</p>
                  <div className="grid grid-cols-2 gap-2.5" data-testid="reserve-plan-cards">
                    {planOptions.map((plan) => (
                      <button
                        key={plan.value}
                        type="button"
                        onClick={() => setPlanInterest(prev => prev === plan.value ? '' : plan.value)}
                        className={`text-left px-4 py-3.5 rounded-xl border transition-all duration-200 group
                          ${planInterest === plan.value
                            ? 'border-[#1A1A1A] bg-[#1A1A1A]'
                            : 'border-black/[0.06] bg-white hover:border-black/15'
                          }`}
                        data-testid={`plan-card-${plan.value.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <p className={`text-sm font-medium leading-tight ${
                          planInterest === plan.value ? 'text-white' : 'text-[#1A1A1A]'
                        }`}>
                          {plan.label}
                        </p>
                        <p className={`text-xs mt-0.5 leading-tight ${
                          planInterest === plan.value ? 'text-white/60' : 'text-[#8A8A8A]'
                        }`}>
                          {plan.desc}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#1A1A1A] text-white py-[16px] rounded-full text-base font-medium
                             hover:bg-[#333] transition-all duration-300 disabled:opacity-50
                             disabled:cursor-not-allowed flex items-center justify-center gap-2.5"
                  data-testid="reserve-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Reserving...
                    </>
                  ) : (
                    'Reserve Early Access'
                  )}
                </button>

                {/* Trust Note */}
                <p className="text-center text-[#8A8A8A] text-sm mt-5" data-testid="reserve-trust-note">
                  No payment required. We'll notify you at launch.
                </p>
              </motion.form>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReservePage;
